import { LogoIntegrityAlert } from '@/components/LogoIntegrityTag';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Card, Typography } from 'antd';
import Link from 'next/link';

const DnsResults = ({ data }) => {
  console.log('data.records.TXT........... ', JSON.stringify(data));
  const recordExists = (record) => {
    if (Array.isArray(record)) {
      return record.length > 0;
    }
    return record && typeof record === 'string' && !record.startsWith('No ');
  };

  const formatMxRecords = (mxRecords) => {
    if (Array.isArray(mxRecords)) {
      return mxRecords.map((mx) => `${mx.exchange} (Priority: ${mx.priority})`).join(', ');
    }
    return 'No MX records found';
  };

  const extractSpfRecord = (txtRecords) => {
    if (!txtRecords || !Array.isArray(txtRecords)) {
      return 'No SPF records found';
    }
    const spfRecord = txtRecords
      .filter(Array.isArray)
      .map((record) => record.join(''))
      .find((record) => record.startsWith('v=spf1'));
    return spfRecord || 'No SPF records found';
  };

  const extractBimiInfo = (bimiRecords) => {
    if (!bimiRecords || !Array.isArray(bimiRecords) || bimiRecords.length === 0) {
      return { bimiUrl: 'No BIMI records found', vmcStatus: 'No Mark Certificate Installed' };
    }

    const bimiRegex = /v=BIMI1;\s*l=([^;]+)(?:;|$)/;
    const vmcRegex = /a=([^;]+)(?:;|$)/;

    for (const record of bimiRecords) {
      const joinedRecord = record.join('');
      const bimiMatch = joinedRecord.match(bimiRegex);
      const vmcMatch = joinedRecord.match(vmcRegex);

      if (bimiMatch) {
        return {
          bimiUrl: bimiMatch[1],
          vmcStatus: vmcMatch ? 'VMC issued' : 'No Mark Certificate Installed',
        };
      }
    }

    return { bimiUrl: 'No BIMI records found', vmcStatus: 'No Mark Certificate Installed' };
  };

  const bimiInfo =
    data.records && data.records.BIMI
      ? extractBimiInfo(data.records.BIMI)
      : { bimiUrl: 'No BIMI records found', vmcStatus: 'No Mark Certificate Installed' };

  // Check if we have certificate info with a Prior Use Mark
  const isCMC =
    data.certificateInfo &&
    (data.certificateInfo.certificateType === 'CMC' ||
      (data.certificateInfo.subject && data.certificateInfo.subject.includes('Prior Use Mark')));

  // Set the correct status based on certificate info
  const certStatus = isCMC ? 'CMC issued' : bimiInfo.vmcStatus;

  const certIssued = certStatus === 'VMC issued' || certStatus === 'CMC issued';
  const logoMismatch = data.certificateInfo?.logoValidation?.valid === false;

  const spfRecord =
    data.records && data.records.TXT ? extractSpfRecord(data.records.TXT) : 'No SPF records found';

  return (
    <div className="dns-results">
      {/* AI Analysis Card - Temporarily commented out */}
      {/* <Card className={styles.aiAnalysisCard} style={{ marginBottom: '24px' }}>
        <h3>AI Analysis:</h3>
        <Typography.Paragraph
          style={{ wordBreak: 'break-word', fontSize: '0.9rem', fontStyle: 'italic' }}
        >
          {data.aiAnalysis || 'No AI analysis available'}
        </Typography.Paragraph>
      </Card> */}

      <LogoIntegrityAlert logoValidation={data.certificateInfo?.logoValidation} />

      <Card style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', marginBottom: '24px' }}>
        <h3>
          VMC/CMC Status:
          {certIssued && !logoMismatch ? (
            <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
          )}
        </h3>
        <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
          {certIssued && logoMismatch ? `${certStatus} — but logo does not match` : certStatus}
        </Typography.Paragraph>

        {certIssued && logoMismatch ? (
          <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
            A {certStatus === 'CMC issued' ? 'Common' : 'Verified'} Mark Certificate is installed,
            but the SVG logo hosted at your BIMI record does not match the logo certified in it.
            Email providers verify this hash and will not display your logo until the hosted file
            matches the certificate.
          </Typography.Paragraph>
        ) : certIssued ? (
          <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
            Congratulations! You have successfully implemented BIMI and purchased a
            {certStatus === 'CMC issued'
              ? ' Common Mark Certificate'
              : ' Verified Mark Certificate'}
            . You can now display your logo inside various email applications.
          </Typography.Paragraph>
        ) : (
          <div className="buttons-row" style={{ width: '40%' }}>
            <Link
              href="/products/digicert-verified-mark-certificate"
              className="btn-primary w-button width-100"
            >
              Buy Now
              <span className="line-rounded-icon link-icon-right"></span>
            </Link>
          </div>
        )}
      </Card>
      <Card style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', marginBottom: '24px' }}>
        <h3>
          BIMI Records:
          {recordExists(data.records && data.records.BIMI) ? (
            <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
          )}
        </h3>
        <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
          {data.records && data.records.BIMI && Array.isArray(data.records.BIMI)
            ? data.records.BIMI.map((record) => record.join(' ')).join(', ')
            : 'No BIMI records found'}
        </Typography.Paragraph>
      </Card>

      <Card style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', marginBottom: '24px' }}>
        <h3>
          DMARC Records:
          {recordExists(data.records && data.records.DMARC) ? (
            <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
          )}
        </h3>
        <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
          {(data.records && data.records.DMARC) || 'No DMARC records found'}
        </Typography.Paragraph>
      </Card>

      <Card style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', marginBottom: '24px' }}>
        <h3>
          MX Records:
          {recordExists(data.records && data.records.MX) ? (
            <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
          )}
        </h3>
        <Typography.Paragraph style={{ wordBreak: 'break-word' }}>
          {data.records && data.records.MX
            ? formatMxRecords(data.records.MX)
            : 'No MX records found'}
        </Typography.Paragraph>
      </Card>

      <Card style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', marginBottom: '24px' }}>
        <h3>
          SPF Records:
          {recordExists(spfRecord) ? (
            <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
          )}
        </h3>
        <Typography.Paragraph style={{ wordBreak: 'break-word' }}>{spfRecord}</Typography.Paragraph>
      </Card>
    </div>
  );
};

export default DnsResults;
